/**
 * Generic Drilldown modal for Dashboard chart clicks.
 * Fetches a filtered list of records and displays them with link-back.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CircleNotch, ArrowSquareOut, FileText, Receipt, ShoppingCart, BookOpen } from '@phosphor-icons/react';
import { useCurrency } from '../../contexts/CompanyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  invoice: { icon: FileText, label: 'Invoices', linkFn: (r) => `/dashboard/sales` },
  quotation: { icon: FileText, label: 'Quotations', linkFn: (r) => `/dashboard/sales` },
  payment_voucher: { icon: Receipt, label: 'Payment Vouchers', linkFn: (r) => `/dashboard/vouchers` },
  receipt_voucher: { icon: Receipt, label: 'Receipt Vouchers', linkFn: (r) => `/dashboard/vouchers` },
  journal_entry: { icon: BookOpen, label: 'Journal Entries', linkFn: (r) => `/dashboard/accounts/journal` },
  purchase_order: { icon: ShoppingCart, label: 'Purchase Orders', linkFn: (r) => `/dashboard/purchases` },
};

export default function DrilldownModal({ open, onClose, title, type, filter }) {
  const { formatCurrency } = useCurrency();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setRows([]);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, JSON.stringify(filter)]);

  const fetchData = async () => {
    try {
      let endpoint = '';
      let params = {};
      switch (type) {
        case 'invoice':
          endpoint = `${API}/invoices`;
          break;
        case 'quotation':
          endpoint = `${API}/documents`;
          params.document_type = 'quotation';
          break;
        case 'payment_voucher':
          endpoint = `${API}/vouchers/payment`;
          break;
        case 'receipt_voucher':
          endpoint = `${API}/vouchers/receipt`;
          break;
        case 'journal_entry':
          endpoint = `${API}/journal-entries`;
          break;
        case 'purchase_order':
          endpoint = `${API}/purchase/documents`;
          params.document_type = 'purchase_order';
          break;
        default:
          throw new Error('Unsupported type');
      }
      const res = await axios.get(endpoint, { params });
      let data = res.data || [];

      // Apply filter client-side (status, date range, account_id)
      if (filter?.status) {
        data = data.filter(r => r.status === filter.status);
      }
      if (filter?.from) {
        data = data.filter(r => (r.date || r.issue_date || r.created_at || '') >= filter.from);
      }
      if (filter?.to) {
        data = data.filter(r => (r.date || r.issue_date || r.created_at || '') < filter.to);
      }
      if (filter?.aging_bucket && type === 'invoice') {
        const today = new Date();
        data = data.filter(r => {
          if (!['approved', 'partial', 'sent', 'draft'].includes(r.status)) return false;
          const due = r.due_date || r.issue_date;
          if (!due) return false;
          const days = Math.floor((today - new Date(due)) / (1000 * 60 * 60 * 24));
          const b = filter.aging_bucket;
          if (b === 'current') return days <= 0;
          if (b === '1_30') return days > 0 && days <= 30;
          if (b === '31_60') return days > 30 && days <= 60;
          if (b === '61_90') return days > 60 && days <= 90;
          if (b === '90_plus') return days > 90;
          return true;
        });
      }
      if (filter?.account_id && type === 'journal_entry') {
        data = data.filter(r => (r.lines || []).some(l => l.account_id === filter.account_id));
      }

      setRows(data.slice(0, 100));
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  const meta = TYPE_META[type] || {};
  const Icon = meta.icon || FileText;

  const totalValue = rows.reduce((s, r) => s + (r.total || r.total_amount || r.total_debit || 0), 0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="drilldown-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-card border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          <div className="px-5 py-3 border-b flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-2">
              <Icon size={20} weight="duotone" className="text-primary" />
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-xs text-muted-foreground">{rows.length} records · Total: {formatCurrency(totalValue)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to={meta.linkFn?.(null) || '/dashboard'} onClick={onClose} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="drilldown-view-all">
                Open module <ArrowSquareOut size={12} />
              </Link>
              <button onClick={onClose} className="p-1.5 rounded hover:bg-foreground/10" data-testid="drilldown-close">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {loading && (
              <div className="flex items-center justify-center py-10">
                <CircleNotch size={28} className="animate-spin text-primary" />
              </div>
            )}
            {error && <p className="text-red-500 text-sm py-6 text-center">{error}</p>}
            {!loading && !error && rows.length === 0 && (
              <p className="text-muted-foreground text-sm py-10 text-center">No records match this filter</p>
            )}
            {!loading && rows.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Date</th>
                    <th className="text-left py-2 px-2">Party / Description</th>
                    <th className="text-left py-2 px-2">Status</th>
                    <th className="text-right py-2 px-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id || i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-2 px-2 font-mono text-xs">{r.document_number || r.voucher_number || r.entry_number || '—'}</td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">{r.date || r.issue_date || (r.created_at || '').split('T')[0]}</td>
                      <td className="py-2 px-2 truncate max-w-[260px]">{r.customer_name || r.supplier_name || r.payee_name || r.received_from || r.description || r.narration || '—'}</td>
                      <td className="py-2 px-2"><span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/50">{r.status}</span></td>
                      <td className="py-2 px-2 text-right font-semibold">{formatCurrency(r.total || r.total_amount || r.total_debit || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
